import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ChevronUp, ChevronDown, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useRefetchOnFocus } from "../../hooks/useRefetchOnFocus";
import { cmsApi, apiError } from "../api";

export function usePageData(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    try { const r = await cmsApi.get(path); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [path]);
  useEffect(() => { reload(); }, [reload]);
  useRefetchOnFocus(reload);
  return { data, setData, loading, reload };
}

export function PageTitle({ title, text, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl text-stone-900">{title}</h1>
        {text && <p className="text-sm text-stone-500 mt-1">{text}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ title, text, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`rounded-2xl bg-white border border-stone-200/80 shadow-sm p-5 sm:p-6 ${className}`}>
      {title && <h2 className="font-serif text-xl text-stone-900">{title}</h2>}
      {text && <p className="text-xs text-stone-500 mt-1 mb-4">{text}</p>}
      {!text && title && <div className="mb-4" />}
      {children}
    </section>
  );
}

export function TextField({ label, value, onChange, type = "text", placeholder, testid, textarea, rows = 4, hint, step, min, max }) {
  const cls = "mt-1 bg-[#FAF7F2]/60";
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wide text-stone-500">{label}</Label>
      {textarea
        ? <Textarea data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} className={cls} />
        : <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} step={step} min={min} max={max} className={cls} />}
      {hint && <p className="text-[11px] text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testid, placeholder = "– bitte wählen –" }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wide text-stone-500">{label}</Label>
      <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} className="mt-1 flex h-9 w-full rounded-md border border-input bg-[#FAF7F2]/60 px-3 py-1 text-sm shadow-sm">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function Toggle({ label, checked, onChange, testid, hint }) {
  return (
    <label className="flex items-center justify-between gap-4 py-2">
      <span><span className="text-sm text-stone-800">{label}</span>{hint && <span className="block text-[11px] text-stone-400">{hint}</span>}</span>
      <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
    </label>
  );
}

export function SaveBar({ onSave, saving, label = "Änderungen speichern", testid = "save-button" }) {
  return (
    <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-8 px-4 sm:px-8 py-3 bg-[#FAF7F2]/90 backdrop-blur border-t border-stone-200/80 mt-8 flex justify-end">
      <Button data-testid={testid} onClick={onSave} disabled={saving} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white rounded-full px-6">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}{saving ? "Speichern…" : label}
      </Button>
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, text, onConfirm, confirmLabel = "Löschen", testid = "confirm-dialog" }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-2xl">{title}</AlertDialogTitle>
          <AlertDialogDescription>{text}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="bg-red-700 hover:bg-red-800">{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function OrderButtons({ onUp, onDown, disableUp, disableDown, testid }) {
  return (
    <div className="flex flex-col">
      <button data-testid={`${testid}-up`} onClick={onUp} disabled={disableUp} className="p-0.5 text-stone-400 hover:text-[#4A1D24] disabled:opacity-20" aria-label="Nach oben"><ChevronUp className="h-4 w-4" /></button>
      <button data-testid={`${testid}-down`} onClick={onDown} disabled={disableDown} className="p-0.5 text-stone-400 hover:text-[#4A1D24] disabled:opacity-20" aria-label="Nach unten"><ChevronDown className="h-4 w-4" /></button>
    </div>
  );
}

export function moveItem(list, index, dir) {
  const next = [...list];
  const j = index + dir;
  if (j < 0 || j >= next.length) return list;
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

export function Spinner() {
  return <div className="flex justify-center py-20" data-testid="page-loading"><Loader2 className="h-6 w-6 animate-spin text-[#4A1D24]" /></div>;
}

export const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
